/* ============================================================
   U2 Subscribe Tiers — drawer toggle + scroll-to-options
                       + hi-res image lightbox
   Path: assets/custom-subscribe-tiers.js
   ============================================================ */

(function () {
  'use strict';

  // Honor user's motion preferences — instant scroll instead of smooth
  // when reduced motion is requested at the OS level.
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Elements that can receive focus, for the lightbox focus trap.
  var FOCUSABLE = 'button:not([disabled]):not([tabindex="-1"]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /**
   * Scroll the first product row inside the opened drawer to roughly
   * mid-viewport. Falls back to the drawer itself if no rows exist
   * (e.g. merchant added a tier card but no product option blocks yet).
   */
  function scrollFirstRowIntoView(drawer) {
    if (!drawer) return;

    var firstRow = drawer.querySelector('.u2-st__row');
    var target = firstRow || drawer;

    // Wait one frame so the browser has actually applied the [hidden]
    // removal + layout reflow before we measure & scroll. Without this,
    // scrollIntoView can land on the pre-open position.
    window.requestAnimationFrame(function () {
      target.scrollIntoView({
        behavior: REDUCED_MOTION ? 'auto' : 'smooth',
        block: 'center',    // centers row vertically — sticky header friendly
        inline: 'nearest'
      });
    });
  }

  /* ============================================================
     LIGHTBOX
     ------------------------------------------------------------
     One overlay per section instance. Triggers carry the hi-res
     URL in data-attributes; we only assign img.src on click, so
     nothing downloads until the user actually asks for it.
     ============================================================ */
  function initLightbox(root) {
    var lb = root.querySelector('[data-u2-st-lightbox]');
    if (!lb) return;                       // lightbox disabled in section settings
    if (lb.dataset.u2LbBound === 'true') return;  // guard against double-binding
    lb.dataset.u2LbBound = 'true';

    var dialog  = lb.querySelector('.u2-st-lb__dialog');
    var img     = lb.querySelector('.u2-st-lb__img');
    var titleEl = lb.querySelector('.u2-st-lb__title');
    var closeBtn = lb.querySelector('.u2-st-lb__close');
    var lastFocused = null;

    if (!dialog || !img || !closeBtn) return;

    function markLoaded() {
      lb.classList.add('u2-st-lb--loaded');
    }

    // Attached once, not per open, so we don't stack listeners.
    img.addEventListener('load', markLoaded);
    img.addEventListener('error', function () {
      // Still reveal the frame so the user isn't stuck on a spinner.
      markLoaded();
      // eslint-disable-next-line no-console
      console.warn('[u2-st-lightbox] image failed to load:', img.currentSrc || img.src);
    });

    function isOpen() {
      return lb.hidden === false;
    }

    function open(trigger) {
      lastFocused = document.activeElement;

      lb.classList.remove('u2-st-lb--loaded');

      titleEl.textContent = trigger.getAttribute('data-lb-title') || '';
      img.setAttribute('alt', trigger.getAttribute('data-lb-alt') || '');

      // Intrinsic width/height prevent a layout jump as the image lands.
      var w = trigger.getAttribute('data-lb-width');
      var h = trigger.getAttribute('data-lb-height');
      if (w && parseInt(w, 10) > 0) { img.setAttribute('width', w); }
      else { img.removeAttribute('width'); }
      if (h && parseInt(h, 10) > 0) { img.setAttribute('height', h); }
      else { img.removeAttribute('height'); }

      // srcset BEFORE src so the browser picks the right candidate
      // on the first pass instead of fetching src then switching.
      img.setAttribute('srcset', trigger.getAttribute('data-lb-srcset') || '');
      img.setAttribute('src', trigger.getAttribute('data-lb-src') || '');

      // Cached images may already be complete — no load event will fire.
      if (img.complete && img.naturalWidth > 0) {
        markLoaded();
      }

      lb.hidden = false;
      document.body.classList.add('u2-st-lb-open');

      window.requestAnimationFrame(function () {
        closeBtn.focus();
      });
    }

    function close() {
      if (!isOpen()) return;
      lb.hidden = true;
      document.body.classList.remove('u2-st-lb-open');

      // src is intentionally NOT cleared — keeps the browser cache warm
      // if the same option is reopened.

      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
      lastFocused = null;
    }

    // Backdrop and X button both carry data-lb-close.
    lb.addEventListener('click', function (evt) {
      if (evt.target.closest('[data-lb-close]')) {
        close();
      }
    });

    // ESC to close + Tab focus trap.
    lb.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' || evt.key === 'Esc') {
        evt.preventDefault();
        close();
        return;
      }

      if (evt.key !== 'Tab') return;

      var items = Array.prototype.slice.call(dialog.querySelectorAll(FOCUSABLE));
      if (items.length === 0) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    });

    // Bind every thumbnail trigger inside this section.
    root.querySelectorAll('[data-u2-st-lb-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function (evt) {
        // The trigger sits inside a drawer, not inside a tier card,
        // so this shouldn't reach the tier handler. Stop it anyway.
        evt.preventDefault();
        evt.stopPropagation();
        open(trigger);
      });
    });
  }

  function init(root) {
    var tiers = root.querySelectorAll('[data-tier]');
    var drawers = root.querySelectorAll('[data-drawer]');

    function closeAll() {
      tiers.forEach(function (t) { t.setAttribute('aria-selected', 'false'); });
      drawers.forEach(function (d) { d.hidden = true; });
    }

    function openTier(which) {
      tiers.forEach(function (t) {
        var match = t.dataset.tier === which;
        t.setAttribute('aria-selected', match ? 'true' : 'false');
      });
      drawers.forEach(function (d) {
        d.hidden = d.dataset.drawer !== which;
      });
    }

    tiers.forEach(function (tier) {
      tier.addEventListener('click', function (evt) {
        // Defensive: if a product-row select button or a lightbox
        // thumbnail click somehow bubbled up to a tier card, ignore it.
        if (evt.target.closest('.u2-st__select-btn')) return;
        if (evt.target.closest('[data-u2-st-lb-open]')) return;

        var which = tier.dataset.tier;
        var alreadyOpen = tier.getAttribute('aria-selected') === 'true';

        if (alreadyOpen) {
          // Re-click on an open tier closes everything. No scroll on close —
          // jumping the page when the user is collapsing the drawer is jarring.
          closeAll();
          return;
        }

        openTier(which);

        // Find the matching drawer (sibling of the tier card in the grid)
        // and scroll its first product row into roughly the viewport center.
        var drawer = root.querySelector('[data-drawer="' + which + '"]');
        scrollFirstRowIntoView(drawer);
      });

      tier.addEventListener('keydown', function (evt) {
        if (evt.key === 'Enter' || evt.key === ' ') {
          evt.preventDefault();
          tier.click();
        }
      });
    });

    initLightbox(root);
  }

  function boot() {
    document.querySelectorAll('[data-u2-subscribe-tiers]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-init for Shopify theme editor (block add/reorder) */
  document.addEventListener('shopify:section:load', function (evt) {
    var section = evt.target.querySelector('[data-u2-subscribe-tiers]');
    if (section) init(section);
  });
})();